import { BlockItem } from "@/models/Blocks/Itens/block-item";
import { BlockItemMedia, BlockMedia } from '@/models/Blocks/Itens/block-item-media';
import { BlockProduct } from "@/models/Blocks/Itens/block-product";
import { BlockCategory } from "@/models/Blocks/Itens/block-category";
import { BlockWhy } from "@/models/Blocks/Itens/block-why";
import { BlockSocial } from "@/models/Blocks/Itens/block-social";
import { BlockContent } from "@/models/Blocks/Itens/block-content";
import { BlockPromo,CouponStruct } from "@/models/Blocks/Itens/block-promo";
import { BlockItemType } from '@/models/Blocks/Itens/Types/block-item-type';
import { BlockBundle, BlockBundleProduct } from '@/models/Blocks/Itens/block-bundle';
import { BlockItemAction, BlockItemContent, BlockItemTimer } from '@/models/Blocks/Itens/Types/block-content-types';
import { SocialLikes,SocialLikesType } from '@/models/Social/social-likes';
import { TemplateItemType } from '@/models/Blocks/Itens/Types/template-item-type';
import { Picture } from "@/models/Muffin/picture";

/**
 * Base input formt
 */
interface CreateInput{
    url?: string,
    cssClass?: string
    backgroundColor?: string,
    extraInfo?: any,
    images?: CreateInputImage[],
    video?: CreateInputVideo,
    borderColor?: string,
    hasBorder?: boolean
    shape?: string,
    debug?: string,
    mub?: string
}

/**
 * Product input formt
 */
interface CreateProductInput extends CreateInput{
    label?: string,
    timer?: number, 
    groupId?: string,
    name: string,
    variant?: string,
    price: string,
    priceCrossed?: string,
    ranking?: number|null,
    page?: number|null,
    transparentMode?: boolean|null,
    isFashion?: boolean,
    imagesHover?: CreateInputImage[]
    videoHover?: CreateInputVideo,
}

interface CreateWhyInput extends CreateInput{
    title: BlockItemContent,
    subtitle?: BlockItemContent, 
}

/**
 * Promo item input formt
 */
interface CreatePromoInput extends CreateInput{
    label?: BlockItemContent,
    title: BlockItemContent,
    subtitle?: BlockItemContent, 
    coupon: string|CouponStruct,
    action: any
}

/**
 * Content item input formt
 */
interface CreateContentInput extends CreateInput{
    icon?: string,
    title: BlockItemContent,
    subtitle?: BlockItemContent, 
    action: any,
    timer?: BlockItemTimer,
    starRating?: number,
    type?: string,
    colorId?: string,
    chatClickId?: string,
}

/**
 * Social item input formt
 */
interface CreateSocialInput extends CreateInput{
    id: number|string,
    name: string,
    desc?: string,
    recordType?: string,
    profile: string,
    parterlUrl?: string, 
    postUrl?: string,
    likes?: SocialLikes,
    openModal?: boolean,
    hasVideo?: boolean,
}

/**
 * Image input formt
 */
interface CreateInputImage{
    url: string,
    condition: string|null
}

/**
 * Video input formt
 */
interface CreateInputVideo{
    url: string,
    poster: string|null
}

/**
 * Bundle product input formt
 */
interface CreateBundleProductInput extends CreateInput{
    groupId?: string,
    name: string
}

/**
 * Bundle input formt
 */
interface CreateBundleInput{
    id: string,
    coupon?: string,
    label?: string,
    timer?: number, 
    price: string,
    priceCrossed?: string,
    firstProduct: CreateBundleProductInput,
    secondProduct: CreateBundleProductInput,
    promoReference?: object,
    cssClass?: string,
    overrideClick?: boolean,
    borderColor?: string,
    hasBorder?: boolean,
    urlDetail?: string,
    transparentMode?: boolean|null
    shape?: string
}

export class BlockFactory {

    /**
     * Return the block shape object
     * @param values 
     * @returns 
     */
    public static get(values: any): BlockItem|null{

        //If already is a block item, just return
        if(values instanceof BlockItem){
            return values;
        }

        let result: BlockItem|null = null;

        try{
            if(values.hasOwnProperty("product") && values.product){            
                result = this._createProduct(values.product);
            }
            if(values.hasOwnProperty("category") && values.category){
                result = this._createCategory(values.category.name,values.category.url,values.category.backgroundColor ?? undefined);
            }
            if(values.hasOwnProperty("why") && values.why){
                result = this._createWhy(values.why);
            }
            if(values.hasOwnProperty("social") && values.social){
                result = this._createSocial(values.social);
            }
            if(values.hasOwnProperty("image") && values.image){
                let item = new BlockItemMedia(BlockItemType.image);
                if(values.image.url ?? null){
                    item.addImage(values.image.url,null,true);
                }
                if(values.image.urlClick ?? null){
                    item.url = values.image.urlClick;
                }
                result = item;
            }
            if(values.hasOwnProperty("content") && values.content){
                result = this._createContent(values.content);
            }
            if(values.hasOwnProperty("promo") && values.promo){
                result = this._createPromo(values.promo);
            }
            if(values.hasOwnProperty("bundle") && values.bundle){
                result = this._createBundle(values.bundle);
            }
            if(values.hasOwnProperty("skeleton") && values.skeleton){
                let item = new BlockItem(BlockItemType.empty)
                item.skeleton = true;
                result = item;
            }
        }catch(e){
            console.log(e);
            //TODO: handle error
            return null;
        }
        if(result){
            result.source = values;
        }
        return result;
    }

    /**
     * Apply colors into the shape
     * @param values 
     * @param colors 
     * @returns 
     */
    public static applyColor(values: any, colors: any): any{
        let obj: any = Object.values(values)[0] ?? null;
        let objType: any = Object.keys(values)[0] ?? null;
        if(obj){
            let type  = obj.type ?? null;
            let color = type ? colors[type] ?? null : null;
            if(type && (colors.id ?? null)){
                obj.colorId = colors.id;
            }
            if(type && !(obj.backgroundColor ?? null) && (colors.background ?? null)){
                obj.backgroundColor = colors.background;
            }
            if((obj.hasBorder??null) && !(obj.borderColor ?? null) && (colors.border ?? null)){
                obj.borderColor = colors.border;
            }
            if(color){
                if((obj.title ?? null) && (typeof obj.title != 'string') && (color.title ?? null) && !(obj.title.color ?? undefined)){
                    obj.title.color = color.title;
                }
                if((obj.subtitle ?? null) && (color.subtitle ?? null) && !(obj.subtitle.color ?? undefined)){
                    obj.subtitle.color = color.subtitle;
                }
                if((obj.action ?? null) && (color.action ?? null) && !(obj.action.color ?? undefined)){
                    obj.action.color = color.action;
                }
                if((obj.label ?? null) && (color.label ?? null) && !(obj.label.color ?? undefined)){
                    obj.label.color = color.label;
                }
                if((obj.coupon ?? null) && (typeof values.coupon != 'string') && (color.coupon ?? null)){
                    if(!(obj.coupon.color ?? null)){
                        obj.coupon.color      = color.coupon.color ?? null;
                    }
                    if(!(obj.coupon.background ?? null)){
                        obj.coupon.background = color.coupon.background ?? null;
                    }
                }
                if((obj.timer) && (obj.timer?.counter ?? null)){
                    if(!(obj.timer.counterColor ?? null)){
                        obj.timer.counterColor = color.time?.color ?? null;
                    }
                    if(!(obj.timer.textColor ?? null)){
                        obj.timer.textColor = color.time?.title ?? null;
                    }
                }
            }
        }
        if(objType){
            values = {[objType]: obj};
        }
        return values;
    }

    /**
     * Create a new bundle shape
     * @param data 
     * @returns 
     */
    protected static _createBundle(data: CreateBundleInput): BlockBundle {
        //first Product
        let first = this._createBundleProduct(data.firstProduct); 

        //Second Product
        let second = this._createBundleProduct(data.secondProduct); 
        
        let promo   = data.label ? {label: data.label, timer: data.timer ?? undefined} : undefined;
        let bundle  = new BlockBundle(data.id,data.coupon ?? '', first,second,{value: data.price, crossed: data.priceCrossed ?? undefined}, promo);
        if(data.promoReference){
            bundle.promoReference = data.promoReference;
        }
        if(data.cssClass){
            bundle.cssClass = data.cssClass;
        }
        if(data.overrideClick){
            bundle.overrideClick = data.overrideClick;
        }
        if(data.borderColor){
            bundle.borderColor = data.borderColor;
        }
        if(data.hasBorder){
            bundle.hasBorder = data.hasBorder;
        }
        if(data.urlDetail){
            bundle.urlDetail = data.urlDetail;
        }
        if(data.transparentMode){
            bundle.transparentMode = data.transparentMode;
        }
        if(data.shape){
            switch(data.shape){
                case 'h':
                    bundle.templateType = TemplateItemType.horizontal;
                    break;
                case 'v':
                    bundle.templateType = TemplateItemType.vertical;
                    break;
                default:
                    bundle.templateType = TemplateItemType.square;
                    break;
            }
        }
        return bundle;
    }

    
    /**
     * Create a new half shape bundle product
     * @param data 
     * @returns 
     */
    protected static _createBundleProduct(data: CreateBundleProductInput): BlockBundleProduct {
        let item = new BlockBundleProduct(data.name, data.groupId);
        item.images = this.CreatePicture(data.images,data.name);
        this._setVideo(data.video,item);
        return item;
    }

    /**
     * Create a new promo shape
     * @param promo 
     * @returns 
     */
    protected static _createPromo(promo: CreatePromoInput): BlockPromo {
        let title    = {text: promo.title.text,color: promo.title.color};
        let subtitle = promo.subtitle ? {text: promo.subtitle.text, color: promo.subtitle.color, icon: promo.subtitle.icon} : undefined;
        let label    = promo.label ? {text: promo.label.text, color: promo.label.color} : undefined;
        let item     = new BlockPromo(title,promo.coupon,subtitle,label);
        if(promo.cssClass){
            item.cssClass = promo.cssClass;
        }
        let action: BlockItemAction|undefined = promo.action ? new BlockItemAction(
            promo.action?.type ?? '',
            promo.action?.value ?? undefined,
            promo.action?.text ?? undefined,
            promo.action?.icon ?? undefined
        ) : undefined;
        if(action){
            item.action = action;
        }
        let alt = promo.title.text ?? null;
        this._setGeneralInfo(promo,item,(alt?this.stripTags(alt):null));
        return item;
    }
    
    /**
     * Create a new product shape
     * @param product 
     * @returns 
     */
    protected static _createProduct(product: CreateProductInput): BlockProduct {
        let promo   = product.label ? {label: product.label, timer: product.timer ?? undefined} : undefined;
        let item    = new BlockProduct(product.name, {value: product.price, crossed: product.priceCrossed ?? undefined}, (product.groupId ?? undefined), promo);
        if(product.ranking) {
            item.ranking = product.ranking;
        }
        if(product.variant) {
            item.variant = product.variant;
        }
        if(product.page) {
            item.page = product.page;
        }
        if(product.transparentMode){
            item.transparentMode = product.transparentMode;
        }
        if(product.isFashion){
            item.isFashion = product.isFashion;
        }
        this._setGeneralInfo(product,item, product.name);
        if(product.videoHover){
            item.hoverVideo = {video: product.videoHover.url, poster: product.videoHover?.poster ?? undefined };
        }
        if(product.imagesHover && product.imagesHover.length > 0){
            item.hoverImages = this.CreatePicture(product.imagesHover,product.name);
        }
        return item;
    }

    /**
     * Create a new category shape
     * @param name 
     * @param url 
     * @param backgroundColor 
     * @returns 
     */
    protected static _createCategory(name: string, url: string, backgroundColor: string|undefined): BlockCategory{
        let item = new BlockCategory(name,url, BlockItemType.category);
        item.backgroundColor = backgroundColor;
        item.url = url;
        return item;
    }

    /**
     * Create a new why shape
     * @param title 
     * @param text 
     * @param backgroundColor 
     * @param cssClass
     * @returns
     */
    protected static _createWhy(value: CreateWhyInput): BlockWhy{
        let title: BlockItemContent = {text:value.title.text,color: value.title.color};
        let subtitle: BlockItemContent = value.subtitle ? {text:value.subtitle.text,color:value.subtitle.color}: {text:''};
        let alt = value.title.text ?? null;
        let item = new BlockWhy(title,subtitle);
        this._setGeneralInfo(value,item,(alt?this.stripTags(alt):null));
        return item;
    }

    /**
     * Create a new content shape (see more, postal code, delivery, etc...)
     * @param value 
     * @returns 
     */
    protected static _createContent(value: CreateContentInput): BlockContent{
        let action: BlockItemAction|undefined = value.action ? new BlockItemAction(
            value.action?.type ?? '',
            value.action?.value ?? undefined,
            value.action?.text ?? undefined,
            value.action?.icon ?? undefined
        ) : undefined;
        if(action && (value.action?.buttonText ?? undefined)){
            action.buttonText = value.action.buttonText;
        }
        let item  = new BlockContent({text: value.title.text,color: value.title.color,icon: value.title.icon ?? undefined },action);
        let title = value.title.text ?? null;
        this._setGeneralInfo(value,item,(title?this.stripTags(title):null));
        if(value.subtitle){
            item.subtitle = {text: value.subtitle.text,color: value.subtitle.color, icon: value.subtitle.icon ?? undefined};
        }
        if(value.icon){
            item.icon = value.icon;
        }
        if(value.timer){
            item.timer = value.timer;
        }
        if(value.starRating){
            item.starRating = value.starRating;
        }
        item.isSeeMore = value.type === 'seeMore';
        if(item.isSeeMore && value.colorId){
            item.colorId = value.colorId;
        }
        if(value.chatClickId){
            item.chatClickId = value.chatClickId;
        }
        return item;
    }

    /**
     * Remove html tags
     * 
     * @param value 
     * @returns 
     */
    public static stripTags(value: string): string{
        return value.replace( /<.*?>/g, '' );
    }

    /**
     * Create a new social shape
     * @param social 
     * @returns 
     */
    protected static _createSocial(social: CreateSocialInput): BlockSocial {
        let item    = new BlockSocial(social.id, social.name);
        this._setGeneralInfo(social,item,social.name);

        item.urls = {page: social.parterlUrl ?? undefined, post: social.postUrl ?? undefined};
        item.profile = social.profile;
        if(social.likes){
            item.likes = new SocialLikes(social.likes.num ?? 0, social.likes.liked ?? false);
            item.likes.id = `${social.id}`;
            item.likes.type = SocialLikesType.MENTION;
            item.likes.showNum = social.likes.showNum ?? true;
        }
        if(social.openModal){
            item.openModal = true;
        }
        if(social.hasVideo){
            item.hasVideo = true;
        }
        item.description = social.desc ? social.desc : undefined;
        item.recordType = social.recordType ? social.recordType : undefined;
        return item;
    }

    /**
     * Set the general info (background, images and video)
     * @param value 
     * @param item 
     */
    protected static _setGeneralInfo(value: CreateInput, item: BlockItem, title: string|null = null): void {
        if(value.backgroundColor){
            item.backgroundColor = value.backgroundColor;
        }
        if(value.url){
            item.url = value.url;
        }
        if(value.cssClass){
            item.cssClass = value.cssClass;
        }
        if(value.extraInfo){
            item.extraInfo = value.extraInfo;
        }
        if(value.borderColor){
            item.borderColor = value.borderColor;
        }
        if(value.hasBorder){
            item.hasBorder = value.hasBorder;
        }
        if(value.debug){
            item.debug = value.debug;
        }
        if(value.mub){
            item.mubData = value.mub;
        }
        if(value.shape){
            switch(value.shape){
                case 'h':
                    item.templateType = TemplateItemType.horizontal;
                    break;
                case 'v':
                    item.templateType = TemplateItemType.vertical;
                    break;
                default:
                    item.templateType = TemplateItemType.square;
                    break;
            }
        }
        if(item instanceof BlockItemMedia){
            item.images = this.CreatePicture(value.images,title);
            this._setVideo(value.video,item);
        }
    }

    /**
     * Create Picture object
     * 
     * @param images 
     * @param title 
     * @returns 
     */
    protected static CreatePicture(images: CreateInputImage[]|String|undefined|null, title: string|null = null): Picture|null{
        let item = new BlockMedia();
        let hasMain = false;
        if(images){
            if(images instanceof String || typeof images == 'string'){
                item.addImage(images.toString(),null,true);
            }else{
                images.forEach((img) => {
                    let isMain = img.condition === null && !hasMain;
                    item.addImage(img.url,img.condition,isMain); 
                    if(isMain){
                        hasMain = isMain;
                    }
                });
            }
            if(item.images && title){
                item.images.title = title;
            }
        }
        return item.images;
    }

    /**
     * Set video into the shape
     * @param value 
     * @param item 
     */
    protected static _setVideo(value: CreateInputVideo|undefined|null, item: BlockMedia){
        if(value?.url){
            item.setVideo(value.url,value?.poster ?? undefined);
        }
    }
}
